import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { DocumentTextIcon } from "@heroicons/react/24/outline";
import OfferDocumentLivePreview from "./offer-document-live-preview";
import OfferPrintLinesPreview from "./offer-print-lines-preview";
import OfferPresentationRulesDialog from "./offer-presentation-rules-dialog";
import { fetchOfferCustomerPresentation, getPdfOffer } from "../../../api/offer";
import { getOfferAsync, updateOfferAsync } from "../../../slices/offer";
import { resolveOfferActionErrorMessage } from "./utils";
import { DEFAULT_OFFER_LINES_VIEW_MODE } from "./utils/offer-lines-view";
import {
  overviewRootCalculationIds,
  prepareCustomerLineItems,
} from "./utils/prepare-customer-line-items";
import toast from "react-hot-toast";

function formatDate(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleDateString("nb-NO");
  } catch {
    return String(value);
  }
}

/**
 * Dokument tab for layout=simple — customer sheet (info + lines), not block builder.
 * Live PDF uses layout=simple. Upgrade CTA persists document_structure defaults.
 */
export default function OfferSimpleDocumentTab({ offer }) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [presentationItems, setPresentationItems] = useState(null);
  const [presentationOpen, setPresentationOpen] = useState(false);
  const [upgrading, setUpgrading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState("");
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const pdfRequestIdRef = useRef(0);

  useEffect(() => {
    if (!offer?.id) return undefined;
    let cancelled = false;
    (async () => {
      const data = await fetchOfferCustomerPresentation(offer.id);
      if (cancelled || data?.missing || data?.error) return;
      if (Array.isArray(data?.items)) setPresentationItems(data.items);
    })();
    return () => {
      cancelled = true;
    };
  }, [offer?.id, offer?.updated_at, offer?.presentation_rules, offer?.calculations]);

  const previewItems = useMemo(() => {
    const source = presentationItems?.length
      ? presentationItems
      : (offer?.calculations || []).length
        ? presentationItems || []
        : offer?.items || [];
    return prepareCustomerLineItems(source, {
      offer,
      presentationRules: offer?.presentation_rules,
      alreadyPresented: Boolean(presentationItems?.length),
    });
  }, [presentationItems, offer]);

  const refreshLivePdf = useCallback(async () => {
    if (!offer?.id) return;
    const requestId = ++pdfRequestIdRef.current;
    setPdfLoading(true);
    setPdfError(null);
    try {
      const response = await getPdfOffer({
        id: offer.id,
        params: { layout: "simple" },
      });
      if (requestId !== pdfRequestIdRef.current) return;
      if (!response?.pdf_url) {
        setPdfError(
          resolveOfferActionErrorMessage(
            t,
            response,
            "offers.documentBuilder.preview.pdf_failed"
          )
        );
        return;
      }
      setPdfUrl(response.pdf_url);
      setPdfError(null);
    } catch (err) {
      if (requestId !== pdfRequestIdRef.current) return;
      setPdfError(
        resolveOfferActionErrorMessage(
          t,
          err,
          "offers.documentBuilder.preview.pdf_failed"
        )
      );
    } finally {
      if (requestId === pdfRequestIdRef.current) {
        setPdfLoading(false);
      }
    }
  }, [offer?.id, t]);

  useEffect(() => {
    if (!offer?.id) return undefined;
    setPdfUrl("");
    setPdfError(null);
    refreshLivePdf();
    return () => {
      pdfRequestIdRef.current += 1;
    };
  }, [offer?.id, refreshLivePdf]);

  const openPdf = async () => {
    try {
      const response = await getPdfOffer({
        id: offer.id,
        params: { layout: "simple" },
      });
      if (response?.pdf_url) {
        window.open(response.pdf_url, "_blank", "noopener,noreferrer");
        return;
      }
      toast.error(
        resolveOfferActionErrorMessage(
          t,
          response,
          "offers.documentBuilder.preview.pdf_failed"
        )
      );
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(
          t,
          err,
          "offers.documentBuilder.preview.pdf_failed"
        )
      );
    }
  };

  const upgradeToDocument = async () => {
    if (!offer?.id || upgrading) return;
    setUpgrading(true);
    try {
      const result = await dispatch(
        updateOfferAsync({ id: offer.id, layout: "document" })
      );
      if (updateOfferAsync.rejected.match(result)) {
        toast.error(t("offers.simpleDocument.upgrade_failed"));
        return;
      }
      await dispatch(getOfferAsync(offer.id));
      toast.success(t("offers.simpleDocument.upgrade_success"));
    } catch {
      toast.error(t("offers.simpleDocument.upgrade_failed"));
    } finally {
      setUpgrading(false);
    }
  };

  if (!offer?.id) {
    return (
      <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 bg-slate-50 px-6 py-16 text-center">
        <DocumentTextIcon className="h-8 w-8 text-slate-300" aria-hidden="true" />
        <p className="text-sm font-medium text-slate-700">
          {t("offers.tabs.document")}
        </p>
        <p className="max-w-md text-sm text-slate-500">
          {t("offers.tabs.no_offer")}
        </p>
      </div>
    );
  }

  const currency = offer.currency || "NOK";
  const metaRows = [
    { label: t("offers.simpleDocument.meta_customer"), value: offer.customer?.name || "—" },
    { label: t("offers.simpleDocument.meta_project"), value: offer.project?.name || "—" },
    { label: t("offers.simpleDocument.meta_reference"), value: offer.reference || "—" },
    {
      label: t("offers.simpleDocument.meta_offer_date"),
      value: formatDate(offer.offer_date),
    },
    {
      label: t("offers.simpleDocument.meta_valid_to"),
      value: formatDate(offer.valid_to),
    },
    {
      label: t("offers.simpleDocument.meta_contact"),
      value:
        offer.contact?.name ||
        [offer.contact?.first_name, offer.contact?.last_name]
          .filter(Boolean)
          .join(" ") ||
        "—",
    },
  ];

  return (
    <div className="relative flex min-h-0 flex-1 flex-col">
      <div className="offer-workspace-tools offer-document-toolbar">
        <span className="rounded-md bg-slate-100 px-2 py-1 text-xs font-semibold text-slate-700">
          {t("offers.workspace.layout_simple")}
        </span>
        <button
          type="button"
          onClick={() => refreshLivePdf()}
          disabled={pdfLoading}
        >
          {t("offers.documentBuilder.preview.refresh")}
        </button>
        <button type="button" onClick={openPdf}>
          {t("offers.workspace.preview")}
        </button>
        <button type="button" onClick={() => setPresentationOpen(true)}>
          {t("offers.presentation.open_button")}
        </button>
        <button
          type="button"
          onClick={upgradeToDocument}
          disabled={upgrading}
          className="offer-workspace-primary"
        >
          {upgrading
            ? t("offers.simpleDocument.upgrading")
            : t("offers.simpleDocument.upgrade")}
        </button>
      </div>

      <div className="flex min-h-0 flex-1 flex-col xl:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden border-b border-slate-200 xl:border-b-0 xl:border-r">
          <section className="shrink-0 border-b border-slate-200 bg-white px-4 py-3">
            <h2 className="text-sm font-semibold text-slate-900">
              {t("offers.simpleDocument.info_title")}
            </h2>
            <p className="mt-0.5 text-xs text-slate-500">
              {t("offers.simpleDocument.info_hint")}
            </p>
            <dl className="mt-3 grid grid-cols-1 gap-2 sm:grid-cols-2">
              {metaRows.map((row) => (
                <div key={row.label} className="min-w-0">
                  <dt className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                    {row.label}
                  </dt>
                  <dd className="truncate text-sm text-slate-900">{row.value}</dd>
                </div>
              ))}
            </dl>
            {(offer.comment || offer.notes) && (
              <div className="mt-3 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700">
                {offer.comment || offer.notes}
              </div>
            )}
          </section>

          <div className="min-h-0 flex-1 overflow-hidden">
            <OfferPrintLinesPreview
              items={previewItems}
              groups={offer.groups || []}
              viewMode={DEFAULT_OFFER_LINES_VIEW_MODE}
              presentationRules={null}
              rootCalculationIds={overviewRootCalculationIds(offer)}
              currency={currency}
              className="offer-print-lines-preview flex h-full min-h-0 flex-col overflow-hidden bg-white"
            />
          </div>
        </div>

        <div className="relative flex min-h-[420px] min-w-0 flex-1 flex-col xl:min-h-0">
          <OfferDocumentLivePreview
            pdfUrl={pdfUrl}
            loading={pdfLoading}
            dirty={false}
            error={pdfError}
            onRefresh={() => refreshLivePdf()}
          />
        </div>
      </div>

      <OfferPresentationRulesDialog
        open={presentationOpen}
        onClose={() => setPresentationOpen(false)}
        offer={offer}
        onSaved={async () => {
          const data = await fetchOfferCustomerPresentation(offer.id);
          if (Array.isArray(data?.items)) setPresentationItems(data.items);
          await refreshLivePdf();
        }}
      />
    </div>
  );
}
